import { createColumnHelper } from '@tanstack/react-table';
import MarketingSourceLogo from 'common/components/icons/marketingSourceLogo';
import { FilterNotApplicable } from 'common/components/table/cell-formatters/filterNotApplicatable';
import { formatIsoDateTimeStringToDisplayDate } from 'common/utils/LEGACY_timeHelpers';
import Blur from 'features/blur/blur';
import { StickyOptions } from 'common/components/table/tableTypes';
import { ConversionInboundCallViewResponse } from '../conversion-inboundcalls.types';
import NavLinkButton from 'common/components/anchors/navlink-button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import MarketingSourceCell from 'common/components/table/cell-formatters/marketing-source-cell';
import CallRecording from '../call-recording/call-recording';
import { PayloadFieldsCellFormatter } from 'common/components/table/cell-formatters/payloadFieldCellFormatter';
import { filterOutStandardQueryFields } from 'common/components/table/cell-formatters/fieldHelpers';
import { KeyValuePair } from 'common/types/key-value-pair';

function createFields(queryStrings: KeyValuePair<string, string>[]): any {
  const fields = queryStrings
    .flatMap((qs, index) => [index, qs])
    .filter((qs) => qs instanceof Object) as any;

  return fields;
}

const columnHelper = createColumnHelper<ConversionInboundCallViewResponse>();

const getCoreColumns = (showConversionId: boolean) => {
  const columns = [
    columnHelper.accessor((row) => row.callStartDate, {
      id: 'callStartDate',
      header: () => <span>Call Start Date</span>,
      enableColumnFilter: false,
      cell: (info) => (
        <span className="font-bold">
          {formatIsoDateTimeStringToDisplayDate(info.getValue())}
        </span>
      )
    }),
    columnHelper.accessor((row) => row.callerNumber, {
      id: 'callerNumber',
      header: () => <span>Caller Number</span>,
      enableColumnFilter: false,
      cell: (info) => (
        <Blur>
          <span>{info.getValue()}</span>
        </Blur>
      )
    }),
    columnHelper.accessor((row) => row, {
      id: 'firstclicksource',
      header: () => <span>First Click Source</span>,
      enableColumnFilter: false,
      cell: (info) => {
        const sources = info.getValue().sources;
        if (sources.length < 1) {
          return '';
        }
        const marketingSource = sources[0];
        const queryStrings =
          info.getValue().firstClick?.firstClickUrlQueryStrings ?? [];

        const fields = createFields(queryStrings);

        return (
          <PayloadFieldsCellFormatter
            additionalFieldsFilter={(fields) =>
              filterOutStandardQueryFields(fields)
            }
            fieldObject={{
              header: marketingSource,
              fields,
              subheader: undefined
            }}
            icon=""
          />
        );
      }
    }),
    columnHelper.accessor((row) => row, {
      id: 'lastclicksource',
      header: () => <span>Last Click Source</span>,
      enableColumnFilter: false,
      cell: (info) => {
        const sources = info.getValue().sources;
        if (sources.length < 1) {
          return <></>;
        }

        const marketingSource = sources[sources.length - 1];

        const queryStrings =
          info.getValue().lastClick?.lastClickUrlQueryStrings ?? [];

        const fields = createFields(queryStrings);

        return (
          <PayloadFieldsCellFormatter
            additionalFieldsFilter={(fields) =>
              filterOutStandardQueryFields(fields)
            }
            fieldObject={{
              header: marketingSource,
              fields,
              subheader: undefined
            }}
            icon=""
          />
        );
      }
    }),
    columnHelper.accessor((row) => row.callResult, {
      id: 'callResult',
      header: () => <span>Call Result</span>,
      enableColumnFilter: false,
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor((row) => row.callDuration, {
      id: 'callDuration',
      header: () => <span>Call Duration</span>,
      enableColumnFilter: false,
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor((row) => row.callLabel, {
      id: 'callLabel',
      header: () => <span>Call Label</span>,
      enableColumnFilter: false,
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor((row) => row.calledNumber, {
      id: 'calledNumber',
      header: () => <span>Called Number</span>,
      enableColumnFilter: false,
      cell: (info) => (
        <Blur>
          <span>{info.getValue()}</span>
        </Blur>
      )
    }),
    columnHelper.accessor((row) => row.callEndDate, {
      id: 'callEndDate',
      header: () => <span>Call End Date</span>,
      enableColumnFilter: false,
      cell: (info) => (
        <span className="font-bold">
          {formatIsoDateTimeStringToDisplayDate(info.getValue())}
        </span>
      )
    })
  ];

  const idColumn = [
    columnHelper.accessor((row) => row.id, {
      id: 'id',
      header: () => <span>Conversion ID</span>,
      enableColumnFilter: false,
      cell: (info) => <span>{info.getValue()}</span>
    })
  ];

  return showConversionId ? [...idColumn, ...columns] : columns;
};

const visitorColumn = columnHelper.accessor((row) => row.visitorId, {
  id: 'Visitor',
  header: () => <span>Visitor</span>,
  enableColumnFilter: false,
  enableSorting: false,
  cell: (info) => {
    return (
      <NavLinkButton
        theme={ButtonTheme.PRIMARY}
        identifier={`view-visitor-${info.getValue()}`}
        to={`/visitors/${info.getValue()}`}
        classOverrides="px-4 py-2 block"
      >
        <span>View</span>
      </NavLinkButton>
    );
  },
  meta: {
    stickyOptions: {
      isEnabled: true,
      side: 'right'
    } as StickyOptions
  },
  enableResizing: false,
  size: 100
});

const callRecordingColumn = columnHelper.accessor(
  (row) => row.hasCallRecording,
  {
    id: 'CallRecording',
    header: () => <span>Recording</span>,
    enableColumnFilter: false,
    enableSorting: false,
    cell: (info) =>
      info.getValue() ? (
        <CallRecording eventId={info.row.original.id}></CallRecording>
      ) : (
        <></>
      ),
    enableResizing: false,
    size: 400
  }
);

export const getConversionInboundCallsColumnDefs = (
  conversionIdToggle: boolean
) => {
  const coreColumns = getCoreColumns(conversionIdToggle);

  const simpleColumns = [...coreColumns, callRecordingColumn, visitorColumn];

  return simpleColumns;
};

export const getConversionInboundCallsDetailedColumnDefs = (
  conversionIdToggle: boolean
) => {
  const coreColumns = getCoreColumns(conversionIdToggle);

  return [
    columnHelper.group({
      id: 'conversion',
      columns: coreColumns
    }),
    columnHelper.group({
      header: 'Web',
      enableColumnFilter: false,
      columns: [
        columnHelper.accessor((row) => row.sources, {
          id: 'sources',
          header: () => <span>Sources</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 400,
          cell: (info) =>
            info.getValue().map((value: string, index: number) => (
              <div key={index} className="flex items-center mr-2">
                <MarketingSourceLogo
                  marketingSource={value}
                  iconClasses="text-lg mr-1 flex"
                  imageClasses="w-4 h-4 mr-1"
                />
                <span>{value}</span>
              </div>
            ))
        })
      ]
    }),
    columnHelper.group({
      header: 'First Click',
      enableColumnFilter: false,
      columns: [
        columnHelper.accessor((row) => row.firstClick?.source, {
          id: 'firstClick.source',
          header: () => <span>Source</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.firstClick?.group, {
          id: 'firstClick.group',
          header: () => <span>Group</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => FilterNotApplicable(info.getValue())
        }),
        columnHelper.accessor((row) => row.firstClick?.keywords, {
          id: 'firstClick.keywords',
          header: () => <span>Keywords</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.firstClick?.landingPage, {
          id: 'firstClick.landingPage',
          header: () => <span>Landing Page</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.firstClick?.referrerHost, {
          id: 'firstClick.referrerHost',
          header: () => <span>Referrer Host</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.firstClick?.referrerPath, {
          id: 'firstClick.referrerPath',
          header: () => <span>Referrer Path</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.firstClick?.canonicalUrl, {
          id: 'firstClick.canonicalUrl',
          header: () => <span>Canonical Url</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 600,
          cell: (info) => <span className="break-all">{info.getValue()}</span>
        }),
        columnHelper.accessor((row) => row.firstClick?.utmCampaign, {
          id: 'firstClick.utmCampaign',
          header: () => <span>Utm Campaign</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.firstClick?.utmContent, {
          id: 'firstClick.utmContent',
          header: () => <span>Utm Content</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.firstClick?.utmMedium, {
          id: 'firstClick.utmMedium',
          header: () => <span>Utm Medium</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.firstClick?.utmSource, {
          id: 'firstClick.utmSource',
          header: () => <span>Utm Source</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.firstClick?.utmTerm, {
          id: 'firstClick.utmTerm',
          header: () => <span>Utm Term</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.firstClick?.utmKeyword, {
          id: 'firstClick.utmKeyword',
          header: () => <span>Utm Keyword</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur>{info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.firstClick?.device, {
          id: 'firstClick.device',
          header: () => <span>Device</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.firstClick?.placement, {
          id: 'firstClick.placement',
          header: () => <span>Placement</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.firstClick?.creative, {
          id: 'firstClick.creative',
          header: () => <span>Creative</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.firstClick?.campaignId, {
          id: 'firstClick.campaignId',
          header: () => <span>Campaign Id</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.firstClick?.adGroupId, {
          id: 'firstClick.adGroupId',
          header: () => <span>Adgroup Id</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.firstClick?.gclid, {
          id: 'firstClick.gclid',
          header: () => <span>Gclid</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 600,
          cell: (info) => (
            <Blur>
              <span className="break-all">{info.getValue()}</span>
            </Blur>
          )
        })
      ]
    }),
    columnHelper.group({
      header: 'Last Click',
      enableColumnFilter: false,
      columns: [
        columnHelper.accessor((row) => row.lastClick?.source, {
          id: 'lastClick.source',
          header: () => <span>Source</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.group, {
          id: 'lastClick.group',
          header: () => <span>Group</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => FilterNotApplicable(info.getValue())
        }),
        columnHelper.accessor((row) => row.lastClick?.keywords, {
          id: 'lastClick.keywords',
          header: () => <span>Keywords</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.landingPage, {
          id: 'lastClick.landingPage',
          header: () => <span>Landing Page</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.referrerHost, {
          id: 'lastClick.referrerHost',
          header: () => <span>Referrer Host</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.referrerPath, {
          id: 'lastClick.referrerPath',
          header: () => <span>Referrer Path</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.canonicalUrl, {
          id: 'lastClick.canonicalUrl',
          header: () => <span>Canonical Url</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 600,
          cell: (info) => <span className="break-all">{info.getValue()}</span>
        }),
        columnHelper.accessor((row) => row.lastClick?.utmCampaign, {
          id: 'lastClick.utmCampaign',
          header: () => <span>Utm Campaign</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.lastClick?.utmContent, {
          id: 'lastClick.utmContent',
          header: () => <span>Utm Content</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.lastClick?.utmMedium, {
          id: 'lastClick.utmMedium',
          header: () => <span>Utm Medium</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.lastClick?.utmSource, {
          id: 'lastClick.utmSource',
          header: () => <span>Utm Source</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.utmTerm, {
          id: 'lastClick.utmTerm',
          header: () => <span>Utm Term</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.lastClick?.utmKeyword, {
          id: 'lastClick.utmKeyword',
          header: () => <span>Utm Keyword</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.lastClick?.device, {
          id: 'lastClick.device',
          header: () => <span>Device</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.placement, {
          id: 'lastClick.placement',
          header: () => <span>Placement</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.creative, {
          id: 'lastClick.creative',
          header: () => <span>Creative</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => info.getValue()
        }),
        columnHelper.accessor((row) => row.lastClick?.campaignId, {
          id: 'lastClick.campaignId',
          header: () => <span>Campaign Id</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.lastClick?.adGroupId, {
          id: 'lastClick.adGroupId',
          header: () => <span>Adgroup Id</span>,
          enableColumnFilter: false,
          enableSorting: false,
          cell: (info) => <Blur> {info.getValue()}</Blur>
        }),
        columnHelper.accessor((row) => row.lastClick?.gclid, {
          id: 'lastClick.gclid',
          header: () => <span>Gclid</span>,
          enableColumnFilter: false,
          enableSorting: false,
          size: 600,
          cell: (info) => (
            <Blur>
              <span className="break-all">{info.getValue()}</span>
            </Blur>
          )
        })
      ]
    }),
    callRecordingColumn,
    visitorColumn
  ];
};

export default getConversionInboundCallsColumnDefs;
